// stores/documentStore.js
import { writable, derived, get } from "svelte/store";
import { format as formatDate } from "date-fns";
import { db } from "../db";
import logger from "../utils/logger";

// Create stores
export const documents = writable([]);
export const currentDocument = writable(null);
export const blocks = writable([]);
export const flashMessage = writable({ type: null, text: null });
export const documentJustLoaded = writable(false);

// Helper function to show flash messages
export function showFlash(type, text) {
  flashMessage.set({ type, text });
  logger.info("Flash message displayed", { type, text });
  setTimeout(() => {
    flashMessage.set({ type: null, text: null });
  }, 3000);
}

// UTILITY FUNCTIONS

// Get today's document if it exists
export function getTodayDocument() {
  const docs = get(documents);
  const todayDateString = formatDate(new Date(), "yyyy-MM-dd");
  const exactTitle = `Journal ${todayDateString}`;
  return docs.find((doc) => doc.title === exactTitle);
}

// DATA OPERATIONS

// Load all documents from the database
export async function loadDocuments() {
  try {
    logger.info("Loading all documents");
    const allDocs = await db.documents.toArray();
    documents.set(allDocs);
    logger.debug("Documents loaded", { count: allDocs.length });
    return allDocs;
  } catch (error) {
    logger.error("Failed to load documents", { error: error.message });
    showFlash("error", "Failed to load documents");
    throw error;
  }
}

// Load a specific document and its blocks
export async function loadDocument(documentId) {
  try {
    logger.info("Loading document", { documentId });
    const doc = await db.documents.get(documentId);

    if (!doc) {
      logger.warn("Document not found", { documentId });
      showFlash("error", "Document not found");
      return null;
    }

    currentDocument.set(doc);

    const documentBlocks = await db.blocks
      .where("documentId")
      .equals(documentId)
      .sortBy("position");

    blocks.set(documentBlocks);

    logger.debug("Document loaded", {
      title: doc.title,
      blockCount: documentBlocks.length,
    });

    // Mark that a document was just loaded
    documentJustLoaded.set(true);

    return doc;
  } catch (error) {
    logger.error("Failed to load document", { error: error.message });
    showFlash("error", "Failed to load document");
    return null;
  }
}

// Create a new document with the given title
export async function createDocument(title) {
  try {
    logger.info("Creating new document", { title });

    // Add the document to the database
    const newDocId = await db.documents.add({
      title,
      createdAt: new Date(),
    });

    // Add initial empty block
    await db.blocks.add({
      documentId: newDocId,
      content: "",
      blockType: "text",
      position: 0,
      createdAt: new Date(),
    });

    // Reload documents
    await loadDocuments();

    logger.info("Document created", { documentId: newDocId, title });
    return newDocId;
  } catch (error) {
    logger.error("Failed to create document", { error: error.message });
    showFlash("error", "Failed to create document");
    return null;
  }
}

// Create today's document
export async function createTodayDocument() {
  try {
    const today = new Date();
    const todayDateString = formatDate(today, "yyyy-MM-dd");
    const docTitle = `Journal ${todayDateString}`;

    // Double-check we don't already have today's document
    const existingDoc = getTodayDocument();

    if (existingDoc) {
      logger.info("Today's document already exists", {
        documentId: existingDoc.id,
      });
      return existingDoc.id;
    }

    // Create the document
    return await createDocument(docTitle);
  } catch (error) {
    logger.error("Failed to create today's document", {
      error: error.message,
    });
    showFlash("error", "Failed to create today's document");
    return null;
  }
}

// Delete a document and its blocks
export async function deleteDocument(documentId) {
  if (!confirm("Are you sure you want to delete this document?")) {
    logger.info("Document deletion cancelled by user", { documentId });
    return false;
  }

  try {
    logger.info("Deleting document", { documentId });
    // Delete all blocks for this document
    await db.blocks.where("documentId").equals(documentId).delete();

    // Delete the document itself
    await db.documents.delete(documentId);

    // Reload documents
    await loadDocuments();
    showFlash("info", "Document deleted successfully");
    logger.info("Document deleted successfully", { documentId });

    return true;
  } catch (error) {
    logger.error("Failed to delete document", {
      error: error.message,
      documentId,
    });
    showFlash("error", "Failed to delete document");
    return false;
  }
}
